import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { pickWorldPoint, type World as PickWorld } from '@viewer/lib/picking'
import { coordFrameFor, coordReadout } from '@viewer/measure/coordReadout'
import {
  makeAnnotationId,
  type Annotation,
} from '@viewer/store/annotationsSlice'

// Renders the contents of the annotations slice into the active world
// and handles the place-mode click flow:
//   - text   : 1 click → place text (uses prompt() for the body).
//   - leader : 2 clicks → arrow from start to head.
//   - cloud  : 2 clicks → bbox corners → cloud rectangle around them.
//
// Each annotation gets a CSS2DObject for its label and (where
// applicable) a Three.Line/Mesh for shapes. We rebuild the whole
// scene group on every annotations change; the list is small and
// rebuilds keep the code simple.

export function useAnnotations(): void {
  const groupRef = useRef<THREE.Group | null>(null)

  // ---- Render annotations into the scene ----
  useEffect(() => {
    let cleanup: (() => void) | null = null
    const setup = () => {
      cleanup?.()
      cleanup = null
      const world = viewerApi.getWorld()
      if (!world) return

      const group = new THREE.Group()
      group.name = 'vn2r-annotations'
      world.scene.three.add(group)
      groupRef.current = group

      const draw = () => {
        while (group.children.length) {
          const c = group.children[0]
          group.remove(c)
          disposeObject(c)
        }
        const annos = useAppStore.getState().annotations
        const sceneSize = computeSceneSize()
        for (const a of annos) drawAnnotation(group, a, sceneSize, world)
      }

      draw()
      const unsub = useAppStore.subscribe((state, prev) => {
        if (state.annotations === prev.annotations) return
        draw()
      })

      cleanup = () => {
        unsub()
        group.parent?.remove(group)
        while (group.children.length) {
          const c = group.children[0]
          group.remove(c)
          disposeObject(c)
        }
        groupRef.current = null
      }
    }
    setup()
    const unsub = viewerApi.subscribe(setup)
    return () => {
      unsub()
      cleanup?.()
    }
  }, [])

  // ---- Place-mode click handling ----
  //
  // Same registration pattern as useWorkPlane (which works reliably
  // for face picking): a single pointerdown listener on the renderer
  // canvas in capture phase. Track the down point and only commit on
  // pointerup with travel < 4 px so camera orbit / pan / marquee
  // don't trigger placements.
  useEffect(() => {
    let cleanup: (() => void) | null = null
    const setup = () => {
      cleanup?.()
      cleanup = null
      const world = viewerApi.getWorld()
      if (!world) return
      const dom = world.renderer?.three.domElement
      if (!dom) return

      // Uses Fragments' built-in raycast (with snap when the user is
      // in coord place mode) instead of THREE.intersectObjects: the
      // latter crashes against Fragments' streamed BufferAttributes
      // ("Cannot read properties of undefined") on click.
      const pick = async (
        clientX: number,
        clientY: number,
      ): Promise<THREE.Vector3 | null> => {
        const s = useAppStore.getState()
        const useSnap = s.annotationPlaceMode === 'coord'
        const result = await pickWorldPoint(
          world,
          dom,
          clientX,
          clientY,
          { snap: useSnap, snapClass: s.snapClass },
        ).catch(() => null)
        if (!result) return null
        return useSnap ? result.snappedPoint.clone() : result.point.clone()
      }

      // Project a world point onto the active work plane (when one is
      // set). Without a work plane, returns the point unchanged. This
      // gives the user predictable placement: pick a face / set a
      // reference plane, and every annotation lives on that plane.
      const projectToWorkPlane = (
        p: THREE.Vector3,
      ): { point: THREE.Vector3; normal: [number, number, number] | null } => {
        const s = useAppStore.getState()
        const wp = s.workPlane
        if (!wp) return { point: p.clone(), normal: null }
        const n = new THREE.Vector3(...wp.normal).normalize()
        // workPlaneSnap=false: keep the plane as visual reference but
        // don't project: the click lands on the geometry exactly
        // where the cursor was, while the plane normal still gets
        // recorded so rect/cloud orient consistently with it.
        if (!s.workPlaneSnap) {
          return { point: p.clone(), normal: [n.x, n.y, n.z] }
        }
        const o = new THREE.Vector3(...wp.origin)
        const v = new THREE.Vector3().subVectors(p, o)
        const d = v.dot(n)
        const projected = p.clone().addScaledVector(n, -d)
        return { point: projected, normal: [n.x, n.y, n.z] }
      }

      const place = (rawP: THREE.Vector3) => {
        const s = useAppStore.getState()
        const mode = s.annotationPlaceMode
        const { point: p, normal } = projectToWorkPlane(rawP)
        if (mode === 'text') {
          const body = window.prompt('Texto da anotação:', '')?.trim()
          if (body) {
            s.addAnnotation({
              id: makeAnnotationId(),
              kind: 'text',
              anchor: [p.x, p.y, p.z],
              text: body,
            })
          }
          s.setAnnotationPlaceMode(null)
          return
        }
        if (mode === 'coord') {
          s.addAnnotation({
            id: makeAnnotationId(),
            kind: 'coord',
            anchor: [p.x, p.y, p.z],
            reference: s.coordsReference ?? null,
            units: s.measureUnits,
          })
          return
        }
        if (mode === 'leader') {
          if (!s.annotationDraftFirstPoint) {
            s.setAnnotationDraftFirstPoint([p.x, p.y, p.z])
            return
          }
          const start = s.annotationDraftFirstPoint
          const body = window.prompt('Texto da seta (vazio = sem texto):', '')
          s.addAnnotation({
            id: makeAnnotationId(),
            kind: 'leader',
            anchor: start,
            tail: [p.x, p.y, p.z],
            text: body?.trim() || undefined,
          })
          s.setAnnotationPlaceMode(null)
          return
        }
        if (mode === 'rect') {
          if (!s.annotationDraftFirstPoint) {
            s.setAnnotationDraftFirstPoint([p.x, p.y, p.z])
            return
          }
          const a = s.annotationDraftFirstPoint
          const cx = (a[0] + p.x) / 2
          const cy = (a[1] + p.y) / 2
          const cz = (a[2] + p.z) / 2
          s.addAnnotation({
            id: makeAnnotationId(),
            kind: 'rect',
            anchor: [cx, cy, cz],
            cornerA: a,
            cornerB: [p.x, p.y, p.z],
            planeNormal: normal ?? undefined,
            filled: false,
          })
          s.setAnnotationPlaceMode(null)
          return
        }
        if (mode === 'cloud') {
          if (!s.annotationDraftFirstPoint) {
            s.setAnnotationDraftFirstPoint([p.x, p.y, p.z])
            return
          }
          const a = s.annotationDraftFirstPoint
          const tag = window.prompt('Etiqueta da nuvem (ex.: R1):', 'R1')
          const cx = (a[0] + p.x) / 2
          const cy = (a[1] + p.y) / 2
          const cz = (a[2] + p.z) / 2
          s.addAnnotation({
            id: makeAnnotationId(),
            kind: 'cloud',
            anchor: [cx, cy, cz],
            cornerA: a,
            cornerB: [p.x, p.y, p.z],
            planeNormal: normal ?? undefined,
            tag: tag?.trim() || undefined,
          })
          s.setAnnotationPlaceMode(null)
          return
        }
      }

      // Same drag-suppression pattern useInteraction uses. We need to
      // track the down across pointermove until pointerup, all on the
      // same listener cluster. Capture-phase on the canvas only; the
      // workplane uses this exact pattern and it works.
      let downX = 0
      let downY = 0
      let downId: number | null = null

      const onDown = (e: PointerEvent) => {
        if (e.button !== 0) return
        const s = useAppStore.getState()
        if (!s.annotationPlaceMode && !s.coordsOriginPickMode) return
        downX = e.clientX
        downY = e.clientY
        downId = e.pointerId
      }

      const onUp = async (e: PointerEvent) => {
        if (e.button !== 0) return
        const s = useAppStore.getState()
        // Coords origin pick: a single click in the canvas captures
        // the world point and stores it as coordsReference. Triggers
        // independently of annotationPlaceMode.
        if (s.coordsOriginPickMode) {
          const dx = Math.abs(e.clientX - downX)
          const dy = Math.abs(e.clientY - downY)
          if (dx <= 4 && dy <= 4) {
            e.preventDefault()
            e.stopPropagation()
            const p = await pick(e.clientX, e.clientY)
            if (p) {
              s.setCoordsReference([p.x, p.y, p.z])
              s.setCoordsOriginPickMode(false)
              downId = null
              return
            }
          }
        }
        if (downId === null || downId !== e.pointerId) return
        const id = downId
        downId = null
        if (!s.annotationPlaceMode) return
        const dx = Math.abs(e.clientX - downX)
        const dy = Math.abs(e.clientY - downY)
        if (dx > 4 || dy > 4) return
        e.preventDefault()
        e.stopPropagation()
        const p = await pick(e.clientX, e.clientY)
        if (!p) return
        place(p)
        void id
      }

      // Window-level fallback: if for any reason the canvas-level
      // listener is intercepted (pointer capture from useInteraction,
      // an overlay on top), this still fires. We hit-test against the
      // canvas rect first.
      const onWindowUp = async (e: PointerEvent) => {
        if (e.button !== 0) return
        const s = useAppStore.getState()
        if (!s.annotationPlaceMode) return
        const rect = dom.getBoundingClientRect()
        if (
          e.clientX < rect.left ||
          e.clientX > rect.right ||
          e.clientY < rect.top ||
          e.clientY > rect.bottom
        ) {
          return
        }
        if (downId !== null && downId === e.pointerId) {
          downId = null
          const dx = Math.abs(e.clientX - downX)
          const dy = Math.abs(e.clientY - downY)
          if (dx > 4 || dy > 4) return
          const p = await pick(e.clientX, e.clientY)
          if (!p) return
          place(p)
        }
      }

      dom.addEventListener('pointerdown', onDown, { capture: true })
      dom.addEventListener('pointerup', onUp, { capture: true })
      window.addEventListener('pointerup', onWindowUp)
      cleanup = () => {
        dom.removeEventListener('pointerdown', onDown, { capture: true })
        dom.removeEventListener('pointerup', onUp, { capture: true })
        window.removeEventListener('pointerup', onWindowUp)
      }
    }
    setup()
    const unsub = viewerApi.subscribe(setup)
    return () => {
      unsub()
      cleanup?.()
    }
  }, [])

  // ---- Esc cancels placement mode ----
  // (kept as a defensive fallback; the central handler in
  // useInteraction.ts viewer.clearOrCancel covers the same cascade)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      const s = useAppStore.getState()
      if (s.annotationPlaceMode) {
        if (s.annotationPlaceMode === 'coord') s.setShowCoordinates(false)
        s.setAnnotationPlaceMode(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // ---- "Apagar uma" bridge: click on a coord label removes it ----
  // The drag handler attached to each coord label by attachLabelDrag
  // would otherwise eat the pointer. We pre-empt it here in capture
  // phase by walking up from the event target until we find an
  // element marked with data-vn2r-anno-kind="coord", reading its
  // data-vn2r-anno-id, and removing the annotation from the store.
  // Single window listener regardless of how many labels are on
  // screen, robust against future re-renders of the label DOM.
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return
      if (!useAppStore.getState().measureDeleteMode) return
      const target = e.target as Element | null
      if (!target) return
      const node =
        typeof target.closest === 'function'
          ? (target.closest(
              '[data-vn2r-anno-kind="coord"]',
            ) as HTMLElement | null)
          : null
      if (!node) return
      const id = node.dataset['vn2rAnnoId']
      if (!id) return
      e.preventDefault()
      e.stopPropagation()
      useAppStore.getState().removeAnnotation(id)
    }
    window.addEventListener('pointerdown', onPointerDown, { capture: true })
    return () =>
      window.removeEventListener('pointerdown', onPointerDown, {
        capture: true,
      })
  }, [])
}

// Helper used by drag handlers: convert a screen-space pixel delta
// into a world-space displacement vector on the camera plane at the
// given world-space anchor depth. Symmetric in x (right) / y (down).
// Wires pointer drag on a CSS2D label element so dragging the label
// re-positions it in 3D space. Anchor stays put (it's the picked
// point); only `offset` mutates. We commit on pointerup; while
// dragging we publish on every pointermove so the label tracks the
// cursor live.
// Build a small drag handle as a CSS2DObject anchored at `initial`.
// `onLive` fires on every pointermove with the current world point;
// `onCommit` once on pointerup. The handle catches its own pointer
// events so it doesn't compete with the canvas listeners.
type HandleStyle = 'corner' | 'endpoint' | 'move'

function makeHandle(
  world: PickWorld,
  initial: THREE.Vector3,
  color: string,
  onLive: (p: THREE.Vector3) => void,
  onCommit: (p: THREE.Vector3) => void,
  options?: { style?: HandleStyle; title?: string; size?: number },
): CSS2DObject {
  const style = options?.style ?? 'endpoint'
  const size = options?.size ?? (style === 'move' ? 18 : 14)
  const el = document.createElement('div')
  el.style.width = `${size}px`
  el.style.height = `${size}px`
  el.style.background = color
  el.style.border = '2px solid #0b0b0c'
  el.style.boxShadow = `0 0 0 1px ${color}, 0 0 4px rgba(0,0,0,0.6)`
  el.style.pointerEvents = 'auto'
  el.style.userSelect = 'none'
  el.style.display = 'flex'
  el.style.alignItems = 'center'
  el.style.justifyContent = 'center'
  el.style.color = '#0b0b0c'
  if (style === 'corner') {
    el.style.borderRadius = '2px'
    el.style.cursor = 'nwse-resize'
    el.title = options?.title ?? 'Arrastar para redimensionar'
  } else if (style === 'move') {
    el.style.borderRadius = '50%'
    el.style.cursor = 'move'
    el.title = options?.title ?? 'Arrastar para mover toda a forma'
    // Tiny "✥" cross-arrows glyph as the move affordance.
    el.innerHTML =
      '<svg viewBox="0 0 24 24" width="10" height="10" stroke="currentColor" stroke-width="3" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M5 9l-3 3 3 3"/><path d="M9 5l3-3 3 3"/><path d="M15 19l-3 3-3-3"/><path d="M19 9l3 3-3 3"/><path d="M2 12h20"/><path d="M12 2v20"/></svg>'
  } else {
    // endpoint: circle with grab cursor (the leader / coord case).
    el.style.borderRadius = '50%'
    el.style.cursor = 'grab'
    el.title = options?.title ?? 'Arrastar a extremidade'
  }
  const obj = new CSS2DObject(el)
  obj.position.copy(initial)
  attachLabelDrag(
    el,
    initial,
    new THREE.Vector3(),
    world,
    (offset) => {
      const p = initial.clone().add(offset)
      obj.position.copy(p)
      onLive(p)
    },
    (offset) => {
      const p = initial.clone().add(offset)
      onCommit(p)
    },
  )
  return obj
}

function attachLabelDrag(
  el: HTMLElement,
  anchor: THREE.Vector3,
  initialOffset: THREE.Vector3,
  world: PickWorld,
  liveUpdate: (offset: THREE.Vector3) => void,
  commit: (offset: THREE.Vector3) => void,
) {
  el.style.cursor = 'grab'
  el.title = (el.title ? el.title + ' · ' : '') + 'Arrastar para reposicionar'
  let dragging = false
  let lastX = 0
  let lastY = 0
  const offset = initialOffset.clone()
  const onDown = (e: PointerEvent) => {
    if (e.button !== 0) return
    dragging = true
    lastX = e.clientX
    lastY = e.clientY
    try {
      el.setPointerCapture(e.pointerId)
    } catch {
      /* ignore */
    }
    el.style.cursor = 'grabbing'
    e.preventDefault()
    e.stopPropagation()
  }
  const onMove = (e: PointerEvent) => {
    if (!dragging) return
    const dx = e.clientX - lastX
    const dy = e.clientY - lastY
    lastX = e.clientX
    lastY = e.clientY
    const labelPos = anchor.clone().add(offset)
    const delta = screenDeltaToWorld(world, labelPos, dx, dy)
    offset.add(delta)
    liveUpdate(offset)
    e.preventDefault()
    e.stopPropagation()
  }
  const onUp = (e: PointerEvent) => {
    if (!dragging) return
    dragging = false
    try {
      el.releasePointerCapture(e.pointerId)
    } catch {
      /* ignore */
    }
    el.style.cursor = 'grab'
    commit(offset)
    e.preventDefault()
    e.stopPropagation()
  }
  el.addEventListener('pointerdown', onDown)
  el.addEventListener('pointermove', onMove)
  el.addEventListener('pointerup', onUp)
  el.addEventListener('pointercancel', onUp)
}

function screenDeltaToWorld(
  world: PickWorld,
  anchor: THREE.Vector3,
  dxPx: number,
  dyPx: number,
): THREE.Vector3 {
  const camera = world.camera.three as THREE.PerspectiveCamera
  const dom = world.renderer?.three.domElement
  const canvasH = dom?.clientHeight ?? 800
  const right = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion)
  const up = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion)
  const camToAnchor = anchor.clone().sub(camera.position)
  const depth = Math.max(0.1, camToAnchor.length())
  const fovYRad = ((camera.fov ?? 60) * Math.PI) / 180
  const worldPerPixel = (2 * Math.tan(fovYRad / 2) * depth) / canvasH
  return right
    .multiplyScalar(dxPx * worldPerPixel)
    .add(up.multiplyScalar(-dyPx * worldPerPixel))
}

function drawAnnotation(
  group: THREE.Group,
  a: Annotation,
  sceneSize: number,
  world: PickWorld,
) {
  const accent = a.color ?? '#f59e0b'
  if (a.kind === 'text') {
    const anchor = new THREE.Vector3(...a.anchor)
    const el = makeTextElement(a.text, accent, a.id)
    const obj = new CSS2DObject(el)
    obj.position.copy(anchor)
    group.add(obj)
    // The label itself is the drag handle for text; no separate
    // dot, the user drags the text directly.
    attachLabelDrag(
      el,
      anchor,
      new THREE.Vector3(),
      world,
      (offset) => {
        obj.position.copy(anchor.clone().add(offset))
      },
      (offset) => {
        const p = anchor.clone().add(offset)
        useAppStore.getState().updateAnnotation(a.id, {
          anchor: [p.x, p.y, p.z],
        })
      },
    )
    return
  }
  if (a.kind === 'leader') {
    // Mutable refs so the drag handlers can update both ends in
    // place without rebuilding the group (rebuild would tear down
    // the handle the user is dragging).
    const start = new THREE.Vector3(...a.anchor)
    const end = new THREE.Vector3(...a.tail)
    const bodyGeom = new THREE.BufferGeometry().setFromPoints([start, end])
    const bodyMat = new THREE.LineBasicMaterial({
      color: new THREE.Color(accent),
      depthTest: false,
      transparent: true,
    })
    const body = new THREE.Line(bodyGeom, bodyMat)
    body.renderOrder = 1000
    group.add(body)

    // Arrow head reused across drags.
    let head: THREE.ArrowHelper | null = null
    const rebuildHead = () => {
      if (head) {
        group.remove(head)
        disposeObject(head)
        head = null
      }
      const dir = new THREE.Vector3().subVectors(start, end).normalize()
      const headLen = Math.max(0.15, start.distanceTo(end) * 0.08)
      head = new THREE.ArrowHelper(
        dir,
        end,
        headLen,
        new THREE.Color(accent).getHex(),
        headLen,
        headLen * 0.5,
      )
      head.line.visible = false
      head.cone.material = (
        head.cone.material as THREE.MeshBasicMaterial
      ).clone()
      ;(head.cone.material as THREE.MeshBasicMaterial).depthTest = false
      head.renderOrder = 1001
      group.add(head)
    }
    rebuildHead()

    let textObj: CSS2DObject | null = null
    if (a.text) {
      textObj = new CSS2DObject(makeTextElement(a.text, accent, a.id))
      textObj.position.copy(start)
      group.add(textObj)
    }

    const updateGeom = () => {
      const pos = body.geometry.attributes['position']
      if (pos) {
        pos.setXYZ(0, start.x, start.y, start.z)
        pos.setXYZ(1, end.x, end.y, end.z)
        pos.needsUpdate = true
      }
      rebuildHead()
      if (textObj) textObj.position.copy(start)
    }

    // Endpoint handles (start = text end / origin, end = arrow tip).
    const startHandle = makeHandle(
      world,
      start,
      accent,
      (p) => {
        start.copy(p)
        updateGeom()
      },
      (p) => {
        useAppStore.getState().updateAnnotation(a.id, {
          anchor: [p.x, p.y, p.z],
        })
      },
      { style: 'endpoint', title: 'Origem da seta (onde fica o texto)' },
    )
    group.add(startHandle)
    const endHandle = makeHandle(
      world,
      end,
      '#fbbf24',
      (p) => {
        end.copy(p)
        updateGeom()
      },
      (p) => {
        useAppStore.getState().updateAnnotation(a.id, {
          tail: [p.x, p.y, p.z],
        })
      },
      { style: 'endpoint', title: 'Ponta da seta' },
    )
    group.add(endHandle)
    // Move handle in the middle: drags both ends in tandem so the
    // user can re-position the whole arrow without changing its
    // direction or length.
    const midInit = new THREE.Vector3().addVectors(start, end).multiplyScalar(
      0.5,
    )
    const lockedStart = start.clone()
    const lockedEnd = end.clone()
    const midHandle = makeHandle(
      world,
      midInit,
      '#94a3b8',
      (p) => {
        const delta = new THREE.Vector3().subVectors(p, midInit)
        const ns = lockedStart.clone().add(delta)
        const ne = lockedEnd.clone().add(delta)
        start.copy(ns)
        end.copy(ne)
        startHandle.position.copy(ns)
        endHandle.position.copy(ne)
        updateGeom()
      },
      (p) => {
        const delta = new THREE.Vector3().subVectors(p, midInit)
        const ns = lockedStart.clone().add(delta)
        const ne = lockedEnd.clone().add(delta)
        useAppStore.getState().updateAnnotation(a.id, {
          anchor: [ns.x, ns.y, ns.z],
          tail: [ne.x, ne.y, ne.z],
        })
      },
      { style: 'move', title: 'Mover a seta inteira' },
    )
    group.add(midHandle)
    return
  }
  if (a.kind === 'coord') {
    // Layout: small cross-hair at the anchor, short leader to the
    // label box. Default offset is ~2% of the visible scene; users
    // can drag the label to reposition.
    const anchor = new THREE.Vector3(...a.anchor)
    const offsetVec = a.labelOffset
      ? new THREE.Vector3(...a.labelOffset)
      : new THREE.Vector3(
          sceneSize * 0.018,
          sceneSize * 0.022,
          sceneSize * 0.018,
        )
    const labelPos = anchor.clone().add(offsetVec)

    // Crosshair on the anchor. Tiny so the dot of the picked point
    // stays visible.
    const cross = new THREE.Group()
    const len = sceneSize * 0.012
    const buildAxis = (dir: THREE.Vector3, color: number) => {
      const p1 = anchor.clone().addScaledVector(dir, -len)
      const p2 = anchor.clone().addScaledVector(dir, len)
      const g = new THREE.BufferGeometry().setFromPoints([p1, p2])
      const m = new THREE.LineBasicMaterial({
        color,
        depthTest: false,
        transparent: true,
        opacity: 0.9,
      })
      const line = new THREE.Line(g, m)
      line.renderOrder = 1001
      return line
    }
    cross.add(buildAxis(new THREE.Vector3(1, 0, 0), 0xef4444))
    cross.add(buildAxis(new THREE.Vector3(0, 1, 0), 0x22c55e))
    cross.add(buildAxis(new THREE.Vector3(0, 0, 1), 0x38bdf8))
    group.add(cross)

    // Leader line from anchor to label. The geometry's second vertex
    // is updated live during drag so the leader follows the label.
    const leaderGeom = new THREE.BufferGeometry().setFromPoints([
      anchor,
      labelPos,
    ])
    const leaderMat = new THREE.LineBasicMaterial({
      color: new THREE.Color(accent),
      depthTest: false,
      transparent: true,
      opacity: 0.7,
    })
    const leader = new THREE.Line(leaderGeom, leaderMat)
    leader.renderOrder = 1000
    group.add(leader)

    const labelEl = makeCoordElement(a, accent)
    const obj = new CSS2DObject(labelEl)
    obj.position.copy(labelPos)
    group.add(obj)
    // Visual hint while "Apagar uma" is active. The actual deletion
    // is wired by a single window-level listener registered once per
    // hook (see useAnnotations bottom); that listener uses closest()
    // on the event target to find the annotation id, which is more
    // reliable than per-label listeners that compete with the drag
    // handler attached by attachLabelDrag.
    const applyDeleteHint = () => {
      const on = useAppStore.getState().measureDeleteMode
      labelEl.style.cursor = on ? 'not-allowed' : 'default'
      labelEl.style.outline = on
        ? '1px solid rgba(248,113,113,0.9)'
        : ''
    }
    applyDeleteHint()
    const unsubDeleteHint = useAppStore.subscribe((s, p) => {
      if (s.measureDeleteMode !== p.measureDeleteMode) applyDeleteHint()
    })
    ;(obj as unknown as { onBeforeRemove?: () => void }).onBeforeRemove =
      () => {
        unsubDeleteHint()
      }
    attachLabelDrag(
      labelEl,
      anchor,
      offsetVec,
      world,
      (live) => {
        // Live update during drag: move the CSS2DObject and the
        // leader's far endpoint without going through the slice. The
        // slice only gets the final offset on pointerup so we don't
        // tear down and rebuild this group on every pointermove.
        const p = anchor.clone().add(live)
        obj.position.copy(p)
        const pos = leader.geometry.attributes['position']
        if (pos) {
          pos.setXYZ(1, p.x, p.y, p.z)
          pos.needsUpdate = true
        }
      },
      (final) => {
        useAppStore.getState().updateAnnotation(a.id, {
          labelOffset: [final.x, final.y, final.z],
        })
      },
    )
    return
  }
  if (a.kind === 'rect' || a.kind === 'cloud') {
    // Mutable corners: drag handlers mutate these and rebuild the
    // shape's geometry in place without going through the slice
    // (avoids tearing down the handle mid-drag).
    const A = new THREE.Vector3(...a.cornerA)
    const B = new THREE.Vector3(...a.cornerB)
    const normalArr = a.planeNormal ?? [0, 1, 0]
    const normal = new THREE.Vector3(...normalArr).normalize()
    let u = new THREE.Vector3(1, 0, 0)
    if (Math.abs(u.dot(normal)) > 0.95) u.set(0, 0, 1)
    u = u.projectOnPlane(normal).normalize()
    const v = new THREE.Vector3().crossVectors(normal, u).normalize()

    const computePoints = (): {
      points: THREE.Vector3[]
      tagPos: THREE.Vector3
      filled: THREE.Vector3[] | null
    } => {
      const centre = new THREE.Vector3()
        .addVectors(A, B)
        .multiplyScalar(0.5)
      const toLocal = (p: THREE.Vector3) => {
        const d = new THREE.Vector3().subVectors(p, centre)
        return { u: d.dot(u), v: d.dot(v) }
      }
      const al = toLocal(A)
      const bl = toLocal(B)
      const minU = Math.min(al.u, bl.u)
      const maxU = Math.max(al.u, bl.u)
      const minV = Math.min(al.v, bl.v)
      const maxV = Math.max(al.v, bl.v)
      const L2W = (lu: number, lv: number) =>
        centre.clone().addScaledVector(u, lu).addScaledVector(v, lv)
      if (a.kind === 'rect') {
        const corners = [
          L2W(minU, minV),
          L2W(maxU, minV),
          L2W(maxU, maxV),
          L2W(minU, maxV),
        ]
        const filled = a.filled
          ? [
              corners[0]!,
              corners[1]!,
              corners[2]!,
              corners[0]!,
              corners[2]!,
              corners[3]!,
            ]
          : null
        return { points: corners, tagPos: L2W(maxU, minV), filled }
      }
      // Cloud
      const w = maxU - minU
      const d = maxV - minV
      const span = Math.max(w, d)
      const r = Math.max(span / 14, sceneSize * 0.005)
      const points: THREE.Vector3[] = []
      const arcSegs = 8
      const addBump = (
        cu: number,
        cv: number,
        nu: number,
        nv: number,
      ) => {
        const baseAngle = Math.atan2(nv, nu)
        for (let i = 0; i <= arcSegs; i++) {
          const t = i / arcSegs
          const ang = baseAngle - Math.PI / 2 + Math.PI * t
          points.push(L2W(cu + r * Math.cos(ang), cv + r * Math.sin(ang)))
        }
      }
      const sides: [number, number, number, number, number, number][] = [
        [minU, minV, maxU, minV, 0, -1],
        [maxU, minV, maxU, maxV, 1, 0],
        [maxU, maxV, minU, maxV, 0, 1],
        [minU, maxV, minU, minV, -1, 0],
      ]
      for (const [u0, v0, u1, v1, nu, nv] of sides) {
        const len = Math.hypot(u1 - u0, v1 - v0)
        const count = Math.max(2, Math.round(len / (r * 1.6)))
        for (let i = 0; i < count; i++) {
          const t = (i + 0.5) / count
          const cu = u0 + (u1 - u0) * t
          const cv = v0 + (v1 - v0) * t
          addBump(cu, cv, nu, nv)
        }
      }
      return { points, tagPos: L2W(maxU, minV), filled: null }
    }

    const { points: outlinePts, tagPos, filled } = computePoints()
    const lineGeom = new THREE.BufferGeometry().setFromPoints(outlinePts)
    const lineMat = new THREE.LineBasicMaterial({
      color: new THREE.Color(accent),
      depthTest: false,
      transparent: true,
      opacity: 0.9,
    })
    const line = new THREE.LineLoop(lineGeom, lineMat)
    line.renderOrder = 1000
    group.add(line)
    let fillMesh: THREE.Mesh | null = null
    if (filled) {
      const fillGeom = new THREE.BufferGeometry().setFromPoints(filled)
      const fillMat = new THREE.MeshBasicMaterial({
        color: new THREE.Color(accent),
        transparent: true,
        opacity: 0.18,
        side: THREE.DoubleSide,
        depthWrite: false,
      })
      fillMesh = new THREE.Mesh(fillGeom, fillMat)
      fillMesh.renderOrder = 999
      group.add(fillMesh)
    }
    const tag = (a as { tag?: string }).tag
    let tagObj: CSS2DObject | null = null
    if (tag) {
      tagObj = new CSS2DObject(makeTagElement(tag, accent, a.id))
      tagObj.position.copy(tagPos)
      group.add(tagObj)
    }

    const rebuildShape = () => {
      const next = computePoints()
      // Replace line geometry: LineLoop count must match the new
      // points length, so swap the whole BufferGeometry.
      line.geometry.dispose()
      line.geometry = new THREE.BufferGeometry().setFromPoints(next.points)
      if (fillMesh && next.filled) {
        fillMesh.geometry.dispose()
        fillMesh.geometry = new THREE.BufferGeometry().setFromPoints(
          next.filled,
        )
      }
      if (tagObj) tagObj.position.copy(next.tagPos)
    }

    // Corner handles: square shape + nwse-resize cursor make it
    // visually clear they resize the bbox. Drag updates the
    // corresponding world point and rebuilds the outline live.
    const handleA = makeHandle(
      world,
      A,
      accent,
      (p) => {
        A.copy(p)
        rebuildShape()
      },
      (p) => {
        useAppStore.getState().updateAnnotation(a.id, {
          cornerA: [p.x, p.y, p.z],
        })
      },
      { style: 'corner', title: 'Canto, arrastar para redimensionar' },
    )
    group.add(handleA)
    const handleB = makeHandle(
      world,
      B,
      accent,
      (p) => {
        B.copy(p)
        rebuildShape()
      },
      (p) => {
        useAppStore.getState().updateAnnotation(a.id, {
          cornerB: [p.x, p.y, p.z],
        })
      },
      { style: 'corner', title: 'Canto oposto, arrastar para redimensionar' },
    )
    group.add(handleB)
    // Move handle in the centre: translates both corners together,
    // so the shape stays the same size and shape, just re-positioned.
    const midInit = new THREE.Vector3().addVectors(A, B).multiplyScalar(0.5)
    const lockedA = A.clone()
    const lockedB = B.clone()
    const midHandle = makeHandle(
      world,
      midInit,
      '#94a3b8',
      (p) => {
        const delta = new THREE.Vector3().subVectors(p, midInit)
        const nA = lockedA.clone().add(delta)
        const nB = lockedB.clone().add(delta)
        A.copy(nA)
        B.copy(nB)
        handleA.position.copy(nA)
        handleB.position.copy(nB)
        rebuildShape()
      },
      (p) => {
        const delta = new THREE.Vector3().subVectors(p, midInit)
        const nA = lockedA.clone().add(delta)
        const nB = lockedB.clone().add(delta)
        useAppStore.getState().updateAnnotation(a.id, {
          cornerA: [nA.x, nA.y, nA.z],
          cornerB: [nB.x, nB.y, nB.z],
        })
      },
      {
        style: 'move',
        title: 'Centro, arrastar para mover a forma sem alterar o tamanho',
      },
    )
    group.add(midHandle)
  }
}

function computeSceneSize(): number {
  const box = new THREE.Box3()
  let initialised = false
  for (const [, model] of viewerApi.iterModels()) {
    const mb = model.box
    if (!mb || mb.isEmpty()) continue
    if (!initialised) {
      box.copy(mb)
      initialised = true
    } else {
      box.union(mb)
    }
  }
  if (!initialised) return 10
  const size = new THREE.Vector3()
  box.getSize(size)
  return Math.max(size.x, size.y, size.z) || 10
}

function makeCoordElement(
  a: Extract<Annotation, { kind: 'coord' }>,
  color: string,
): HTMLDivElement {
  const factor: Record<'mm' | 'cm' | 'm' | 'km', number> = {
    mm: 1000,
    cm: 100,
    m: 1,
    km: 0.001,
  }
  const digits =
    a.units === 'mm' ? 0 : a.units === 'cm' ? 1 : a.units === 'm' ? 3 : 5
  const fmt = (v: number) => (v * factor[a.units]).toFixed(digits)
  const sign = (v: number) => (v >= 0 ? '+' : '')
  const el = document.createElement('div')
  el.dataset['vn2rAnnoId'] = a.id
  el.dataset['vn2rAnnoKind'] = 'coord'
  el.style.padding = '3px 6px'
  el.style.borderRadius = '4px'
  // Translucent dark background: geometry behind the label stays
  // partially readable. backdrop-filter blurs whatever is behind so
  // the text reads even over busy meshes.
  el.style.background = 'rgba(11,11,12,0.55)'
  el.style.backdropFilter = 'blur(3px)'
  ;(el.style as unknown as { webkitBackdropFilter: string }).webkitBackdropFilter =
    'blur(3px)'
  el.style.border = `1px solid ${color}`
  el.style.color = '#e5e7eb'
  el.style.font = '600 10px ui-monospace, monospace'
  el.style.lineHeight = '1.35'
  el.style.whiteSpace = 'nowrap'
  el.style.pointerEvents = 'auto'
  el.style.cursor = 'default'
  el.style.userSelect = 'none'
  // O âncora fica gravado em coordenadas do mundo three (é o que os
  // viewpoints e o BCF guardam); só a LEITURA passa para a convenção do
  // IFC, com os mesmos rótulos do HUD (X/Y/Z do ficheiro ou E/N/Cota).
  const st = useAppStore.getState()
  const frame = coordFrameFor(
    st.models.map((m) => m.id),
    st.geoBaselines,
    st.geoOverrides,
  )
  const r = a.reference
  const abs = coordReadout(a.anchor, frame)
  const delta = r
    ? coordReadout(
        [a.anchor[0] - r[0], a.anchor[1] - r[1], a.anchor[2] - r[2]],
        frame,
      )
    : null
  const colours = ['#f87171', '#4ade80', '#38bdf8']
  const rows = abs
    .map((ax, i) => {
      const d = delta
        ? ` <span style="color:#94a3b8">(Δ ${sign(delta[i].value)}${fmt(delta[i].value)})</span>`
        : ''
      return `<div><span style="color:${colours[i]}">${ax.label}</span> ${fmt(ax.value)}${d}</div>`
    })
    .join('')
  el.innerHTML =
    `<div style="font-weight:700;color:${color};margin-bottom:2px;">${a.units}</div>` +
    rows
  // Deletion is handled by the "Apagar uma" mode in the Medições
  // ribbon (measureDeleteMode). When that mode is active, clicks on
  // the label fall through to the canvas and useMeasure picks them
  // up via screen-space proximity to the anchor. Keep this label
  // simple, no inline delete affordance.
  return el
}

function makeTextElement(text: string, color: string, id: string): HTMLDivElement {
  const el = document.createElement('div')
  el.dataset['vn2rAnnoId'] = id
  el.style.padding = '2px 6px'
  el.style.borderRadius = '3px'
  el.style.background = 'rgba(11,11,12,0.55)'
  el.style.backdropFilter = 'blur(3px)'
  ;(el.style as unknown as { webkitBackdropFilter: string }).webkitBackdropFilter =
    'blur(3px)'
  el.style.border = `1px solid ${color}`
  el.style.color = color
  el.style.font = '600 11px ui-sans-serif, system-ui, sans-serif'
  el.style.whiteSpace = 'nowrap'
  el.style.pointerEvents = 'auto'
  el.style.cursor = 'default'
  el.style.userSelect = 'none'
  el.textContent = text
  return el
}

function makeTagElement(tag: string, color: string, id: string): HTMLDivElement {
  const el = document.createElement('div')
  el.dataset['vn2rAnnoId'] = id
  el.style.padding = '1px 5px'
  el.style.borderRadius = '999px'
  el.style.background = color
  el.style.color = '#0b0b0c'
  el.style.font = '700 10px ui-monospace, monospace'
  el.style.pointerEvents = 'auto'
  el.style.cursor = 'default'
  el.style.userSelect = 'none'
  el.textContent = tag
  return el
}

function disposeObject(obj: THREE.Object3D) {
  obj.traverse((o) => {
    const hook = (o as unknown as { onBeforeRemove?: () => void })
      .onBeforeRemove
    if (typeof hook === 'function') hook()
    const m = o as THREE.Mesh & THREE.LineSegments
    if (m.geometry) m.geometry.dispose()
    const mat = m.material as
      | THREE.Material
      | THREE.Material[]
      | undefined
    if (Array.isArray(mat)) {
      for (const x of mat) x.dispose()
    } else if (mat) {
      mat.dispose()
    }
  })
}
